import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  EstabelecimentoDataService,
  EstabelecimentoService,
  ProfissionalDataService,
  ProfissionalService,
  BardoContextDataService,
  NichoTermosService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { BottomSheetComponent, DeviceService } from '@nomoh-monorepo/shared-common-ui';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import { IProfissional, PerfilAcessoBardo } from '@nomoh-monorepo/shared-types';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ProfissionalViewModalComponent } from './modals/profissional-view-modal/profissional-view-modal.component';

/**
 * Componente de listagem de profissionais
 */
@Component({
  selector: 'lib-profissionais',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ChipModule,
    ConfirmDialogModule,
    DialogModule,
    TooltipModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    SelectModule,
    FormsModule,
    ProfissionalViewModalComponent,
    BottomSheetComponent,
    TranslateModule,
  ],
  templateUrl: './profissionais.component.html',
  styleUrls: ['./profissionais.component.scss'],
})
export class ProfissionaisComponent implements OnInit {
  private profissionalService = inject(ProfissionalService);
  private profissionalDataService = inject(ProfissionalDataService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private estabelecimentoService = inject(EstabelecimentoService);
  private nichoTermosService = inject(NichoTermosService);
  private confirmationService = inject(ConfirmationService);
  private contextoService = inject(BardoContextDataService);
  private notificacaoService = inject(NotificacaoService);
  private translateService = inject(TranslateService);
  deviceService = inject(DeviceService);

  $termos = this.nichoTermosService.$termos;

  modalExclusaoAberto = signal(false);
  profissionalParaExcluir = signal<IProfissional | null>(null);
  excluindo = signal(false);
  modalTrocaAberto = signal(false);
  profissionalParaReativar = signal<IProfissional | null>(null);
  pausarId = signal<string | null>(null);
  trocando = signal(false);

  // Filtro de busca local
  pesquisa = signal('');

  // Signals
  $profissionais = this.profissionalDataService.$profissionais;
  $loading = this.profissionalDataService.$loading;
  $estabelecimentoAtual = this.estabelecimentoDataService.$estabelecimentoAtual;
  $planoQuotas = this.estabelecimentoDataService.$planoQuotas;

  $atingiuLimiteProfissionais = computed(() => {
    const max = this.$planoQuotas()?.maxProfissionais;
    if (max == null) return false;
    return this.profissionalDataService.$profissionaisNaQuota() >= max;
  });

  $mensagemLimiteProfissionais = computed(() => {
    const quotas = this.$planoQuotas();
    if (!quotas?.maxProfissionais) return '';
    const pausados = this.profissionalDataService.$profissionaisPausados().length;
    if (pausados > 0) {
      return `O plano ${quotas.nome ?? ''} permite ${
        quotas.maxProfissionais
      } ativo(s). ${pausados} cadastro(s) pausado(s) — troque a vaga ou faça upgrade.`;
    }
    return `O plano ${quotas.nome ?? ''} permite no máximo ${
      quotas.maxProfissionais
    } profissional(is) ativo(s). Faça upgrade para cadastrar mais.`;
  });

  $qtdOperacionais = computed(
    () => this.profissionalDataService.$profissionaisOperacionais().length
  );
  $qtdPausados = computed(
    () => this.profissionalDataService.$profissionaisPausados().length
  );

  $candidatosPausar = computed(() => {
    const reativarId = this.profissionalParaReativar()?.id;
    return this.profissionalDataService
      .$profissionaisOperacionais()
      .filter(
        (p) =>
          p.id !== reativarId &&
          (p.perfilAcesso === 'PROFISSIONAL' || !p.perfilAcesso)
      );
  });

  $profissionaisFiltrados = computed(() => {
    const todos = this.$profissionais() || [];
    const termo = this.pesquisa().trim().toLowerCase();
    if (!termo) return todos;

    return todos.filter((p) => {
      const nome = (p.nome || '').toLowerCase();
      const desc = (p.descricao || '').toLowerCase();
      return nome.includes(termo) || desc.includes(termo);
    });
  });
  
  possuiPermissao(permissao: string) {
    return this.contextoService.possuiPermissao(permissao);
  }

  modalViewAberto = signal(false);
  modalEditAberto = signal(false);
  profissionalSelecionado = signal<IProfissional | null>(null);
  skeletonCards = [1, 2, 3];
  private avatarPalette = [
    '#6D28D9',
    '#2563EB',
    '#0EA5E9',
    '#059669',
    '#F97316',
    '#EA580C',
  ];

  ngOnInit() {
    this.carregarProfissionais();
    if (!this.$planoQuotas()) {
      void this.estabelecimentoService.getPlanoQuotas();
    }
  }

  async carregarProfissionais() {
    const estabelecimentoId = this.$estabelecimentoAtual()?.id;
    if (!estabelecimentoId) return;

    try {
      await this.profissionalService.findAll({ estabelecimentoId });
    } catch (err) {
      console.error(err);
    }
  }

  abrirModalNovo() {
    this.profissionalSelecionado.set(null);
    this.modalEditAberto.set(true);
  }

  abrirModalView(profissional: IProfissional | null = null) {
    if (!profissional && this.$atingiuLimiteProfissionais()) {
      return;
    }
    this.profissionalSelecionado.set(profissional);
    this.modalViewAberto.set(true);
  }

  abrirModalEdit(profissional: IProfissional, event?: Event) {
    event?.stopPropagation();
    this.profissionalSelecionado.set(profissional);
    this.modalEditAberto.set(true);
  }

  fecharModalView() {
    this.modalViewAberto.set(false);
    this.profissionalSelecionado.set(null);
  }

  fecharModalEdit() {
    this.modalEditAberto.set(false);
    this.profissionalSelecionado.set(null);
  }

  async onSalvar() {
    this.fecharModalEdit();
    await this.carregarProfissionais();
  }

  /**
   * Confirma exclusão de profissional a partir de um clique na lista
   * (requer event para posicionar o dialog)
   */
  confirmarExclusao(profissional: IProfissional, event: Event) {
    event.stopPropagation();
    if (this.deviceService.$isMobile()) {
      this.profissionalParaExcluir.set(profissional);
      this.modalExclusaoAberto.set(true);
    } else {
      this.confirmationService.confirm({
        target: event.target as EventTarget,
        message: this.translateService.instant('BARDO_PROFISSIONAIS.CONFIRMAR_EXCLUSAO_MSG', {
          termo: this.$termos().profissional.singular.toLowerCase(),
          nome: profissional.nome,
        }),
        header: this.translateService.instant('GLOBAL.CONFIRMAR_EXCLUSAO'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translateService.instant('GLOBAL.SIM_EXCLUIR'),
        rejectLabel: this.translateService.instant('GLOBAL.CANCELAR'),
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => this.excluirProfissional(profissional.id),
      });
    }
  }

  excluirDoModal(profissional: IProfissional) {
    if (this.deviceService.$isMobile()) {
      this.profissionalParaExcluir.set(profissional);
      this.modalExclusaoAberto.set(true);
    } else {
      this.confirmationService.confirm({
        message: this.translateService.instant('BARDO_PROFISSIONAIS.CONFIRMAR_EXCLUSAO_MSG', {
          termo: this.$termos().profissional.singular.toLowerCase(),
          nome: profissional.nome,
        }),
        header: this.translateService.instant('GLOBAL.CONFIRMAR_EXCLUSAO'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translateService.instant('GLOBAL.SIM_EXCLUIR'),
        rejectLabel: this.translateService.instant('GLOBAL.CANCELAR'),
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => this.excluirProfissional(profissional.id),
      });
    }
  }

  async excluirProfissional(id?: string) {
    if (!id) return;
    this.excluindo.set(true);
    try {
      await this.profissionalService.deletar(id);
      this.modalExclusaoAberto.set(false);
      this.profissionalParaExcluir.set(null);
      await this.carregarProfissionais();
    } catch (err) {
      console.error('Erro ao excluir profissional', err);
    } finally {
      this.excluindo.set(false);
    }
  }

  getIniciais(nome: string): string {
    return nome
      .split(' ')
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  getImagemPerfilUrl(profissional: IProfissional): string | null {
    if (!profissional.imagemPerfil || !profissional.typeImage) {
      return null;
    }
    return `data:${profissional.typeImage};base64,${profissional.imagemPerfil}`;
  }

  getTextoDias(profissional: IProfissional): string {
    const dias = profissional.diasSemanaPresente || [];
    if (dias.length === 7) return this.translateService.instant('BARDO_PROFISSIONAIS.TODOS_OS_DIAS');
    if (dias.length === 0) return this.translateService.instant('BARDO_PROFISSIONAIS.NENHUM_DIA');

    const diasOrdenados = dias
      .map((d) => Number(d.diaId))
      .sort((a, b) => a - b);
    const nomesKeys = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];
    return diasOrdenados.map((id) => this.translateService.instant(`BARDO_PROFISSIONAIS.DIAS.${nomesKeys[id - 1]}`)).join(', ');
  }

  getAvatarColor(profissionalId: number | string | undefined): string {
    const idNumerico = Number(profissionalId);
    if (!idNumerico) {
      return this.avatarPalette[0];
    }

    const index = idNumerico % this.avatarPalette.length;
    return this.avatarPalette[index];
  }

  perfilLabel(perfil: PerfilAcessoBardo): string {
    return this.translateService.instant(`BARDO_PROFISSIONAIS.PERFIS.${perfil}`);
  }

  confirmarRevogarAcesso(event: Event, profissional: IProfissional) {
    event.stopPropagation();
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translateService.instant('BARDO_PROFISSIONAIS.CONFIRMAR_REVOGAR_MSG', { nome: profissional.nome }),
      header: this.translateService.instant('BARDO_PROFISSIONAIS.CONFIRMAR_REVOGAR_TITULO'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('GLOBAL.SIM_REVOGAR'),
      rejectLabel: this.translateService.instant('GLOBAL.CANCELAR'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.revogarAcesso(profissional),
    });
  }

  private async revogarAcesso(profissional: IProfissional) {
    try {
      await this.profissionalService.revogarAcesso(profissional.id as string);
    } catch {
      // Notificação já é exibida pelo interceptor HTTP global.
    }
  }

  estaPausado(profissional: IProfissional): boolean {
    return profissional.status === false;
  }

  precisaConverterParaProfissional(profissional: IProfissional): boolean {
    const quotas = this.$planoQuotas();
    if (!quotas) return false;
    if (profissional.perfilAcesso === 'RECEPCAO' && !quotas.allowRecepcao) {
      return true;
    }
    if (
      profissional.perfilAcesso === 'ADMINISTRADOR' &&
      !quotas.allowAdminExtra
    ) {
      return true;
    }
    return false;
  }

  textoUsarVaga(profissional: IProfissional): string {
    const termo = this.$termos().profissional.singular;
    const converter = this.precisaConverterParaProfissional(profissional);
    const vagaLivre = !this.$atingiuLimiteProfissionais();
    if (vagaLivre && converter) {
      return this.translateService.instant('BARDO_PROFISSIONAIS.ATIVAR_NO_PLANO_CONVERTE', { termo });
    }
    if (vagaLivre) {
      return this.translateService.instant('BARDO_PROFISSIONAIS.ATIVAR_NO_PLANO');
    }
    if (converter) {
      return this.translateService.instant('BARDO_PROFISSIONAIS.USAR_VAGA_CONVERTE', { termo });
    }
    return this.translateService.instant('BARDO_PROFISSIONAIS.USAR_VAGA');
  }

  async solicitarReativar(profissional: IProfissional, event: Event) {
    event.stopPropagation();
    if (!profissional.id) return;

    const converter = this.precisaConverterParaProfissional(profissional);
    const vagaLivre = !this.$atingiuLimiteProfissionais();

    if (vagaLivre) {
      try {
        await this.profissionalService.atualizar(profissional.id, {
          status: true,
          ...(converter ? { perfilAcesso: 'PROFISSIONAL' as const } : {}),
        });
        this.notificacaoService.notificacaoSuccess(
          converter
            ? `${profissional.nome} está no plano como Profissional.`
            : `${profissional.nome} voltou a ficar ativo no plano.`
        );
      } catch {
        // interceptor
      }
      return;
    }

    this.profissionalParaReativar.set(profissional);
    const candidatos = this.$candidatosPausar();
    if (!candidatos.length) {
      try {
        await this.profissionalService.atualizar(profissional.id, {
          status: true,
          ...(converter ? { perfilAcesso: 'PROFISSIONAL' as const } : {}),
        });
        this.notificacaoService.notificacaoSuccess(
          `${profissional.nome} voltou a ficar ativo no plano.`
        );
      } catch {
        // interceptor
      }
      this.profissionalParaReativar.set(null);
      return;
    }

    this.pausarId.set((candidatos[0]?.id as string) ?? null);
    this.modalTrocaAberto.set(true);
  }

  fecharModalTroca() {
    if (this.trocando()) return;
    this.modalTrocaAberto.set(false);
    this.profissionalParaReativar.set(null);
    this.pausarId.set(null);
  }

  onVisibleTrocaChange(visible: boolean) {
    if (!visible) {
      this.fecharModalTroca();
    }
  }

  async confirmarTrocaVaga() {
    const reativar = this.profissionalParaReativar();
    const pausar = this.pausarId();
    if (!reativar?.id || !pausar) return;
    this.trocando.set(true);
    try {
      await this.profissionalService.trocarVaga(reativar.id, pausar);
      this.modalTrocaAberto.set(false);
      this.profissionalParaReativar.set(null);
      this.pausarId.set(null);
      this.notificacaoService.notificacaoSuccess(
        `${reativar.nome} está ativo no plano. O outro cadastro foi pausado.`
      );
    } catch {
      // interceptor
    } finally {
      this.trocando.set(false);
    }
  }
}
